import React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { connect } from 'react-redux';
import { Input, Select, FallbackImage, Render, Button, Table, Pagination, DatePicker, PaperClip, DocUpload, Modal, Viewer } from 'components';
import { RootState } from 'reducers';
import { Globals, Constants, Colors } from 'utils';
import { VehicleService } from 'services';
import moment from 'moment';
import { saveDraftToDB, loadDraftFromDB, clearDraftFromDB } from 'utils/vehicleDraftDB';
import { UploadIcon, EyeIcon, CheckCircleIcon, CancelCircleIcon, TruckIcon, CameraIcon, CheckTruckIcon } from 'assets/icons';
import { TruckImageLeft, TruckImageRight, TruckImageBack, TruckImageFront, FinishRegisterVehicleImage, Logo } from 'assets/img';

interface Props extends RootState, RouteComponentProps<any> {
	dispatch: any
}

const STEPS = {
	FILES: 1,
	MODEL: 2,
	DETAILS: 3,
	ACCESSORIES: 4,
	DOCUMENTS: 5,
	PHOTOS: 6,
	APPOINTMENT: 7
}

const photos = [
	{
		name: 'Vista Frontal',
		field: 'photo_front',
		icon: TruckImageFront
	},
	{
		name: 'Vista Posterior',
		field: 'photo_back',
		icon: TruckImageBack
	},
	{
		name: 'Vista Lateral 1',
		field: 'photo_left',
		icon: TruckImageLeft
	},
	{
		name: 'Vista Lateral 2',
		field: 'photo_right',
		icon: TruckImageRight
	}
];

const files = [
	{
		name: 'Póliza de Seguro',
		field: 'insurance_policy'
	},
	{
		name: 'Registro Sanitario',
		field: 'health_registration'
	},
	{
		name: 'Carnet de Circulación',
		field: 'vehicle_card'
	},
	{
		name: 'ROTC',
		field: 'rotc'
	},
	{
		name: 'INSOPESCA',
		field: 'insopesca'
	},
	{
		name: 'RACDA',
		field: 'racda'
	},
	{
		name: 'RESQUIMC',
		field: 'resquimc'
	}
]

class Create extends React.Component<Props> {

	timer: any = undefined;

	state = {
		form: {
			insurance_policy: {
				file: null,
				expiration_date: ''
			},
			health_registration: {
				file: null,
				expiration_date: ''
			},
			vehicle_card: {
				file: null,
				expiration_date: ''
			},
			rotc: {
				file: null,
				expiration_date: ''
			},
			insopesca: {
				file: null,
				expiration_date: ''
			},
			racda: {
				file: null,
				expiration_date: ''
			},
			resquimc: {
				file: null,
				expiration_date: ''
			},
			photo_front: {
				file: null
			},
			photo_back: {
				file: null
			},
			photo_left: {
				file: null
			},
			photo_right: {
				file: null
			},
			purchase_title: {
				file: null
			},
			plate_file: {
				file: null
			},
			title_owner: true,
			brand: '',
			model: '',
			year: '',
			color: '',
			plate: '',
			tires: '',
			category: '',
			agency_id: '',
			search_model: '',
			search_type: '',
			vehicle_model_id: '',
			vehicle_model_type_id: '',
			length: '',
			width: '',
			height: '',
			platform_length: '',
			platform_width: '',
			temperature: ''
		},
		step: STEPS.FILES,
		accessories: [],
		visible: false,
		visible_file: false,
		file: null,
		agencies: [],
		vehicle: null,
		models: [],
		types: [],
		accessory_types: []
	}

	componentDidMount() {
		this.props.dispatch({
			type: 'SET_HEADER',
			payload: {
				title: `${this.props.match.params?.id ? 'Editar' : 'Añadir'} vehículo`,
				onBack: null
			}
		});

		this.props.dispatch({
			type: 'SET_SUBHEADER',
			payload: {
				title: ''
			}
		});

		// Solo restaurar draft en modo creación (no edición).
		// Es clave esperar que load() termine antes de restoreDraft() para evitar
		// que el setState de load() sobreescriba los accesorios restaurados.
		if (!this.props.match.params?.id) {
			this.load().then(() => this.restoreDraft());
		} else {
			this.load();
		}
	}

	/**
	 * Persiste en IndexedDB el estado completo del formulario + el step actual.
	 * IndexedDB usa el Structured Clone Algorithm, por lo que soporta
	 * nativamente File, Blob, ArrayBuffer — no se necesita convertir a Base64.
	 */
	saveDraft = async () => {
		const { form, step, accessories } = this.state;
		const f: any = form;
		const userId = this.props.user?.id;
		console.log('[CREATE VEHICLE] 🔍 Intentando guardar draft...');
		console.log('[CREATE VEHICLE] 👤 User ID:', userId);
		console.log('[CREATE VEHICLE] 📍 Step actual:', step);
		if (!userId) {
			console.warn('[saveDraft] No hay usuario autenticado, no se puede guardar draft');
			return;
		}
		await saveDraftToDB({
			step,
			accessories,
			form: {
				// --- Campos de texto / IDs ---
				title_owner: f.title_owner,
				brand: f.brand,
				model: f.model,
				year: f.year,
				color: f.color,
				plate: f.plate,
				tires: f.tires,
				category: f.category,
				agency_id: f.agency_id,
				search_model: f.search_model,
				search_type: f.search_type,
				vehicle_model_id: f.vehicle_model_id,
				vehicle_model_type_id: f.vehicle_model_type_id,
				length: f.length,
				width: f.width,
				height: f.height,
				platform_length: f.platform_length,
				platform_width: f.platform_width,
				temperature: f.temperature,
				// --- Archivos (File / Blob nativos) ---
				vehicle_card_file: f.vehicle_card?.file ?? null,
				plate_file_file: f.plate_file?.file ?? null,
				purchase_title_file: f.purchase_title?.file ?? null,
				photo_front_file: f.photo_front?.file ?? null,
				photo_back_file: f.photo_back?.file ?? null,
				photo_left_file: f.photo_left?.file ?? null,
				photo_right_file: f.photo_right?.file ?? null,
				insurance_policy_file: f.insurance_policy?.file ?? null,
				insurance_policy_expiration_date: f.insurance_policy?.expiration_date
					? moment(f.insurance_policy.expiration_date).toISOString() : '',
				health_registration_file: f.health_registration?.file ?? null,
				health_registration_expiration_date: f.health_registration?.expiration_date
					? moment(f.health_registration.expiration_date).toISOString() : '',
				rotc_file: f.rotc?.file ?? null,
				rotc_expiration_date: f.rotc?.expiration_date
					? moment(f.rotc.expiration_date).toISOString() : '',
				insopesca_file: f.insopesca?.file ?? null,
				insopesca_expiration_date: f.insopesca?.expiration_date
					? moment(f.insopesca.expiration_date).toISOString() : '',
				racda_file: f.racda?.file ?? null,
				racda_expiration_date: f.racda?.expiration_date
					? moment(f.racda.expiration_date).toISOString() : '',
				resquimc_file: f.resquimc?.file ?? null,
				resquimc_expiration_date: f.resquimc?.expiration_date
					? moment(f.resquimc.expiration_date).toISOString() : '',
			}
		}, userId);
	}

	/** Restaura desde IndexedDB si existe un draft guardado */
	restoreDraft = async () => {
		const userId = this.props.user?.id;
		console.log('[CREATE VEHICLE] 🔄 Intentando restaurar draft...');
		console.log('[CREATE VEHICLE] 👤 User ID:', userId);
		if (!userId) {
			console.warn('[restoreDraft] No hay usuario autenticado, no se puede cargar draft');
			return;
		}
		const draft = await loadDraftFromDB(userId);
		console.log('[CREATE VEHICLE] 📦 Draft recibido:', draft ? '✅ Existe' : '❌ No existe');
		if (!draft?.form) return;
		const df = draft.form;

		// Merge de accesorios: usar la metadata fresca del API (que ya esta en state
		// tras el await load()) pero aplicar los valores (true/false) guardados en el draft.
		// Esto evita perder los iconos, nombres y tipos que vienen del servidor.
		const mergedAccessories = this.state.accessories.map((acc: any) => {
			const saved = (draft.accessories || []).find((d: any) => d._id === acc._id);
			return saved !== undefined ? { ...acc, value: saved.value } : acc;
		});

		this.setState({
			step: draft.step || STEPS.FILES,
			accessories: mergedAccessories.length > 0 ? mergedAccessories : this.state.accessories,
			form: {
				...this.state.form,
				title_owner: df.title_owner ?? true,
				brand: df.brand || '',
				model: df.model || '',
				year: df.year || '',
				color: df.color || '',
				plate: df.plate || '',
				tires: df.tires || '',
				category: df.category || '',
				agency_id: df.agency_id || '',
				search_model: df.search_model || '',
				search_type: df.search_type || '',
				vehicle_model_id: df.vehicle_model_id || '',
				vehicle_model_type_id: df.vehicle_model_type_id || '',
				length: df.length || '',
				width: df.width || '',
				height: df.height || '',
				platform_length: df.platform_length || '',
				platform_width: df.platform_width || '',
				temperature: df.temperature || '',
				// --- Restaurar archivos nativos desde IndexedDB ---
				vehicle_card: {
					...this.state.form.vehicle_card,
					file: df.vehicle_card_file ?? null,
				},
				plate_file: {
					...this.state.form.plate_file,
					file: df.plate_file_file ?? null,
				},
				purchase_title: {
					...this.state.form.purchase_title,
					file: df.purchase_title_file ?? null,
				},
				photo_front: {
					...this.state.form.photo_front,
					file: df.photo_front_file ?? null,
				},
				photo_back: {
					...this.state.form.photo_back,
					file: df.photo_back_file ?? null,
				},
				photo_left: {
					...this.state.form.photo_left,
					file: df.photo_left_file ?? null,
				},
				photo_right: {
					...this.state.form.photo_right,
					file: df.photo_right_file ?? null,
				},
				insurance_policy: {
					...this.state.form.insurance_policy,
					file: df.insurance_policy_file ?? null,
					expiration_date: df.insurance_policy_expiration_date
						? moment(df.insurance_policy_expiration_date).toDate() : '',
				},
				health_registration: {
					...this.state.form.health_registration,
					file: df.health_registration_file ?? null,
					expiration_date: df.health_registration_expiration_date
						? moment(df.health_registration_expiration_date).toDate() : '',
				},
				rotc: {
					...this.state.form.rotc,
					file: df.rotc_file ?? null,
					expiration_date: df.rotc_expiration_date
						? moment(df.rotc_expiration_date).toDate() : '',
				},
				insopesca: {
					...this.state.form.insopesca,
					file: df.insopesca_file ?? null,
					expiration_date: df.insopesca_expiration_date
						? moment(df.insopesca_expiration_date).toDate() : '',
				},
				racda: {
					...this.state.form.racda,
					file: df.racda_file ?? null,
					expiration_date: df.racda_expiration_date
						? moment(df.racda_expiration_date).toDate() : '',
				},
				resquimc: {
					...this.state.form.resquimc,
					file: df.resquimc_file ?? null,
					expiration_date: df.resquimc_expiration_date
						? moment(df.resquimc_expiration_date).toDate() : '',
				},
			}
		});
	}

	/** Elimina el draft de IndexedDB */
	clearDraft = () => {
		const userId = this.props.user?.id;
		if (!userId) {
			console.warn('[clearDraft] No hay usuario autenticado');
			return;
		}
		clearDraftFromDB(userId);
	}

	load = async (withoutLoading = false) => {
		const res: any = await VehicleService.view({
			vehicle_id: this.props.match.params?.id
		});
		if (res.vehicle) {
			await this.setState({
				vehicle: res.vehicle,
				form: {
					...this.state.form,
					brand: res.brand || '',
					model: res.vehicle?.model || '',
					year: res.vehicle?.year || '',
					color: res.vehicle?.color || '',
					plate: res.vehicle?.plate || '',
					category: res.vehicle?.category || '',
					tires: res.vehicle?.tires || '',
					title_owner: res.vehicle?.title_owner,
					agency_id: res.vehicle?.agency || '',
					vehicle_model_id: res.vehicle?.vehicle_model || '',
					vehicle_model_type_id: res.vehicle?.vehicle_model_type || '',
					length: res.vehicle?.length || '',
					width: res.vehicle?.width || '',
					height: res.vehicle?.height || '',
					platform_length: res.vehicle?.platform_length || '',
					platform_width: res.vehicle?.platform_width || '',
					temperature: res.vehicle?.temperature || '',
					insurance_policy: (() => {
						const default_file = res.vehicle?.files?.find((i: any) => i.type == Constants.VEHICLE_FILES.INSURANCE_POLICY);
						return {
							defaultValue: default_file,
							file: null,
							expiration_date: default_file?.expiration_date ? moment(default_file?.expiration_date).toDate() : ''
						}
					})(),
					health_registration: (() => {
						const default_file = res.vehicle?.files?.find((i: any) => i.type == Constants.VEHICLE_FILES.HEALTH_REGISTRATION);
						return {
							defaultValue: default_file,
							file: null,
							expiration_date: default_file?.expiration_date ? moment(default_file?.expiration_date).toDate() : ''
						}
					})(),
					vehicle_card: (() => {
						const default_file = res.vehicle?.files?.find((i: any) => i.type == Constants.VEHICLE_FILES.VEHICLE_CARD);
						return {
							defaultValue: default_file,
							file: null,
							expiration_date: default_file?.expiration_date ? moment(default_file?.expiration_date).toDate() : ''
						}
					})(),
					rotc: (() => {
						const default_file = res.vehicle?.files?.find((i: any) => i.type == Constants.VEHICLE_FILES.ROTC);
						return {
							defaultValue: default_file,
							file: null,
							expiration_date: default_file?.expiration_date ? moment(default_file?.expiration_date).toDate() : ''
						}
					})(),
					insopesca: (() => {
						const default_file = res.vehicle?.files?.find((i: any) => i.type == Constants.VEHICLE_FILES.INSOPESCA);
						return {
							defaultValue: default_file,
							file: null,
							expiration_date: default_file?.expiration_date ? moment(default_file?.expiration_date).toDate() : ''
						}
					})(),
					racda: (() => {
						const default_file = res.vehicle?.files?.find((i: any) => i.type == Constants.VEHICLE_FILES.RACDA);
						return {
							defaultValue: default_file,
							file: null,
							expiration_date: default_file?.expiration_date ? moment(default_file?.expiration_date).toDate() : ''
						}
					})(),
					resquimc: (() => {
						const default_file = res.vehicle?.files?.find((i: any) => i.type == Constants.VEHICLE_FILES.RESQUIMC);
						return {
							defaultValue: default_file,
							file: null,
							expiration_date: default_file?.expiration_date ? moment(default_file?.expiration_date).toDate() : ''
						}
					})(),
					photo_front: {
						defaultValue: res.vehicle?.files?.find((i: any) => i.type == Constants.VEHICLE_FILES.PHOTO_FRONT),
						file: null
					},
					photo_back: {
						defaultValue: res.vehicle?.files?.find((i: any) => i.type == Constants.VEHICLE_FILES.PHOTO_BACK),
						file: null
					},
					photo_left: {
						defaultValue: res.vehicle?.files?.find((i: any) => i.type == Constants.VEHICLE_FILES.PHOTO_LEFT),
						file: null
					},
					photo_right: {
						defaultValue: res.vehicle?.files?.find((i: any) => i.type == Constants.VEHICLE_FILES.PHOTO_RIGHT),
						file: null
					},
					purchase_title: {
						defaultValue: res.vehicle?.files?.find((i: any) => i.type == Constants.VEHICLE_FILES.PURCHASE_TITLE),
						file: null
					},
					plate_file: {
						defaultValue: res.vehicle?.files?.find((i: any) => i.type == Constants.VEHICLE_FILES.PLATE),
						file: null
					}
				}
			});
		}
		await new Promise<void>(resolve =>
			this.setState({
				accessories: res.accessories?.map((item: any) => {
					if (res.vehicle)
						item.value = res.vehicle?.accessories?.map((i: any) => i.accessory).indexOf(item._id) != -1;
					return item;
				}) || [],
				accessory_types: res.accessory_types || [],
				agencies: res.agencies || [],
				models: res.models || [],
				types: res.types || []
			}, resolve)
		);
	}

	change = (e: any, callback = () => { }) => {
		this.setState({
			form: {
				...this.state.form,
				[e.target.name]: e.target.value
			}
		}, callback);
	}

	onChangeFile = async (value: any, target: string) => {
		const onError = (msg: string) => {
			Globals.showError(msg);
			return false;
		}

		if (target == 'vehicle_card') {
			const res: any = await VehicleService.readFile({
				hasFile: true,
				file: value,
				user_id: this.props.user?.id,
				vehicle_id: this.props.match.params?.id || '',
			});
			if (res.result) {
				if (!res.plate) {
					return onError('No se pudo leer la placa del documento, por favor inténtelo nuevamente');
				}
				this.setState({
					form: {
						...this.state.form,
						brand: res.brand || '',
						model: res.model || '',
						year: res.year || '',
						color: res.color || '',
						plate: res.plate || '',
						category: res.category || '',
						plate_file: {
							...this.state.form.plate_file,
							file: null
						}
					}
				});
			}
			else {
				this.setState({
					form: {
						...this.state.form,
						vehicle_card: {
							...this.state.form.vehicle_card,
							file: null
						}
					}
				});
				return onError('El documento cargado no corresponde al formato requerido. Por favor, sube el documento solicitado para continuar.');
			}
		}
		else if (target == 'plate_file') {
			const res: any = await VehicleService.readFile({
				hasFile: true,
				file: value,
				user_id: this.props.user?.id,
				vehicle_id: this.props.match.params?.id || '',
				document_type_id: Constants.BRAVEALO_DOCUMENT_TYPES.PLATE,
				plate: this.state.form.plate
			});
			if (!res.result) {
				this.setState({
					form: {
						...this.state.form,
						plate_file: {
							...this.state.form.plate_file,
							file: null
						}
					}
				});
				return onError('No se pudo comprobar la foto de la placa. Por favor, asegúrese de que sea visible y se visualice el mismo número de placa que en el certificado de registro de vehículo');
			}
		}

		this.setState({
			form: {
				...this.state.form,
				[target]: {
					// @ts-ignore
					...this.state.form[target],
					file: value
				}
			}
		});
	}

	changeDocument = async (item: any) => {
		const { form } = this.state;
		const target = item.field;

		const onError = (msg: string) => {
			Globals.showError(msg);
			return false;
		}

		if (['health_registration', 'insurance_policy', 'racda', 'insopesca', 'rotc', 'resquimc'].indexOf(target) != -1) {
			let document_type_id = null;
			let name = '';
			let ignore_document_expiration = false;

			switch (target) {
				case 'health_registration':
					document_type_id = Constants.BRAVEALO_DOCUMENT_TYPES.HEALTH_PERMIT;
					name = 'Permiso Sanitario';
					ignore_document_expiration = true;
					break;

				case 'insurance_policy':
					document_type_id = Constants.BRAVEALO_DOCUMENT_TYPES.RCV;
					name = 'Poliza de Seguro';
					break;

				case 'insopesca':
					document_type_id = Constants.BRAVEALO_DOCUMENT_TYPES.INSOPESCA;
					name = 'INSOPESCA';
					ignore_document_expiration = true;
					break;

				case 'rotc':
					document_type_id = Constants.BRAVEALO_DOCUMENT_TYPES.ROCT;
					name = 'ROTC';
					break;

				case 'racda':
					document_type_id = Constants.BRAVEALO_DOCUMENT_TYPES.RACDA;
					name = 'RACDA';
					ignore_document_expiration = true;
					break;

				case 'resquimc':
					document_type_id = Constants.BRAVEALO_DOCUMENT_TYPES.RESQUIMC;
					name = 'RESQUIMC';
					ignore_document_expiration = true;
					break;
			}

			const res: any = await VehicleService.readFile({
				hasFile: true,
				// @ts-ignore
				file: form[item.field]?.file?.value,
				user_id: this.props.user?.id,
				vehicle_id: this.props.match.params?.id || '',
				document_type_id,
				plate: this.state.form.plate,
				ignore_document_expiration: ignore_document_expiration || ''
			});
			if (!res.result || !res.expiration_date) {
				this.setState({
					form: {
						...this.state.form,
						[target]: {
							// @ts-ignore
							...this.state.form[target],
							file: null
						}
					}
				});
				return onError(`Parece que este archivo no corresponde al documento requerido o no es válido. Por favor, carga un(a) ${name}`);
			}
			else {
				this.setState({
					form: {
						...this.state.form,
						[target]: {
							// @ts-ignore
							...this.state.form[target],
							expiration_date: res.expiration_date ? moment(res.expiration_date).toDate() : ''
						}
					}
				});
			}
		}
	}

	back = () => {
		const { step } = this.state;

		switch (step) {
			case STEPS.FILES:
				this.clearDraft(); // Cancelar registro limpia el draft
				this.props.history.replace(`/${this.props.user?.level_id == Constants.LEVELS.CLIENT ? 'client' : 'moderators'}/vehicles`)
				break;

			case STEPS.MODEL:
				this.setState({ step: STEPS.FILES });
				break;

			case STEPS.DETAILS:
				this.setState({ step: STEPS.MODEL });
				break;

			case STEPS.ACCESSORIES:
				this.setState({ step: STEPS.DETAILS });
				break;

			case STEPS.PHOTOS:
				this.setState({ step: STEPS.ACCESSORIES });
				break;

			case STEPS.DOCUMENTS:
				this.setState({ step: STEPS.PHOTOS });
				break;

			case STEPS.APPOINTMENT:
				this.setState({ step: STEPS.DOCUMENTS });
				break;
		}
	}

	next = async () => {
		const { step } = this.state;
		const form: any = this.state.form;
		const is_edit = this.props.match.params?.id != null;

		switch (step) {
			case STEPS.FILES:
				if ((!form.vehicle_card?.file && !form.vehicle_card?.defaultValue) || (!form.plate_file?.file && !form.plate_file?.defaultValue) || ((!form.purchase_title?.file && !form.purchase_title?.defaultValue) && !form.title_owner)) {
					Globals.showError("Por favor, ingrese todos los campos para continuar");
					return;
				}
				this.setState({ step: STEPS.MODEL }, () => { if (!is_edit) this.saveDraft(); });
				break;

			case STEPS.MODEL:
				if (!form.vehicle_model_id || !form.vehicle_model_type_id) {
					Globals.showError("Por favor, seleccione el modelo y el tipo de vehículo para continuar");
					return;
				}
				this.setState({ step: STEPS.DETAILS }, () => { if (!is_edit) this.saveDraft(); });
				break;

			case STEPS.DETAILS:
				// @ts-ignore
				if (this.state.form.temperature && isNaN(this.state.form.temperature)) {
					Globals.showError("El campo temperatura no es válido");
					return;
				}
				this.setState({ step: STEPS.ACCESSORIES }, () => { if (!is_edit) this.saveDraft(); });
				break;

			case STEPS.ACCESSORIES:
				if (!form.tires) {
					Globals.showError("Por favor, ingrese la condición de los cauchos para continuar");
					return;
				}
				this.setState({ step: STEPS.PHOTOS }, () => { if (!is_edit) this.saveDraft(); });
				break;

			case STEPS.PHOTOS:
				if (
					(!form.photo_front?.file && !form.photo_front?.defaultValue) ||
					(!form.photo_back?.file && !form.photo_back?.defaultValue) ||
					(!form.photo_left?.file && !form.photo_left?.defaultValue) ||
					(!form.photo_right?.file && !form.photo_right?.defaultValue)
				) {
					Globals.showError("Por favor, ingrese todas las fotos para continuar");
					return;
				}
				this.setState({ step: STEPS.DOCUMENTS }, () => { if (!is_edit) this.saveDraft(); });
				break;

			case STEPS.DOCUMENTS:
				if (
					((form.insurance_policy?.file || form.insurance_policy?.defaultValue) && !form.insurance_policy?.expiration_date) ||
					((form.health_registration?.file || form.health_registration?.defaultValue) && !form.health_registration?.expiration_date) ||
					((form.insopesca?.file || form.insopesca?.defaultValue) && !form.insopesca?.expiration_date) ||
					((form.racda?.file || form.racda?.defaultValue) && !form.racda?.expiration_date) ||
					((form.rotc?.file || form.rotc?.defaultValue) && !form.rotc?.expiration_date) ||
					((form.resquimc?.file || form.resquimc?.defaultValue) && !form.resquimc?.expiration_date)
				) {
					Globals.showError("Por favor, seleccione la fecha de expiración de los documentos cargados");
					return;
				}
				if (is_edit) {
					this.finish();
				}
				else {
					this.setState({ step: STEPS.APPOINTMENT }, () => { if (!is_edit) this.saveDraft(); });
				}
				break;

			case STEPS.APPOINTMENT:
				if (!form.agency_id) {
					Globals.showError("Por favor, seleccione la agencia para continuar");
					return;
				}
				if (!is_edit) this.saveDraft();
				this.finish();
				break;
		}
	}

	finish = async () => {
		const form: any = this.state.form;

		let files_required = ['rotc', 'insurance_policy'];
		for (let i = 0; i < files_required.length; i++) {
			// @ts-ignore
			if (!this.state.form[files_required[i]].file && !this.state.form[files_required[i]].defaultValue) {
				let _document = '';
				switch (files_required[i]) {
					case 'insurance_policy': _document = 'Poliza de Seguro'; break;
					case 'rotc': _document = 'ROTC'; break;
				}

				Globals.showError(`El documento ${_document} es requerido`);
				return;
			}
		}

		await VehicleService.save({
			hasFile: true,
			user_id: this.props.user?.id,
			vehicle_id: this.props.match.params?.id || '',
			...this.state.form,
			vehicle_card: form.vehicle_card?.file,
			vehicle_card_expiration_date: form.vehicle_card?.expiration_date && moment(form.vehicle_card?.expiration_date).format('YYYY-MM-DD'),
			plate_file: form.plate_file?.file,
			purchase_title: form.purchase_title?.file,
			insurance_policy: form.insurance_policy?.file?.value,
			insurance_policy_expiration_date: form.insurance_policy?.expiration_date && moment(form.insurance_policy?.expiration_date).format('YYYY-MM-DD'),
			health_registration: form.health_registration?.file?.value,
			health_registration_expiration_date: form.health_registration?.expiration_date && moment(form.health_registration?.expiration_date).format('YYYY-MM-DD'),
			insopesca: form.insopesca?.file?.value,
			insopesca_expiration_date: form.insopesca?.expiration_date && moment(form.insopesca?.expiration_date).format('YYYY-MM-DD'),
			racda: form.racda?.file?.value,
			racda_expiration_date: form.racda?.expiration_date && moment(form.racda?.expiration_date).format('YYYY-MM-DD'),
			rotc: form.rotc?.file?.value,
			rotc_expiration_date: form.rotc?.expiration_date && moment(form.rotc?.expiration_date).format('YYYY-MM-DD'),
			resquimc: form.resquimc?.file?.value,
			resquimc_expiration_date: form.resquimc?.expiration_date && moment(form.resquimc?.expiration_date).format('YYYY-MM-DD'),
			photo_back: form.photo_back?.file?.value,
			photo_front: form.photo_front?.file?.value,
			photo_right: form.photo_right?.file?.value,
			photo_left: form.photo_left?.file?.value,
			accessories: JSON.stringify(this.state.accessories.filter((i: any) => i.value))
		});
		if (this.props.match.params?.id) {
			Globals.showSuccess("Se ha modificado el vehículo correctamente");
			this.onClose();
		}
		else {
			this.clearDraft(); // Limpiar draft al finalizar exitosamente
			this.setState({ visible: true });
		}
	}

	onClose = () => {
		this.clearDraft();
		this.props.history.replace(`/${this.props.user?.level_id == Constants.LEVELS.CLIENT ? 'client' : 'moderators'}/vehicles`);
	}

	viewFile = (item: any) => {
		if (typeof item === 'string') {
			if (item.endsWith('.pdf')) {
				window.open(item, '_blank');
			}
			else {
				this.setState({
					visible_file: true,
					file: item
				});
			}
		}
		else if (item instanceof File) {
			if (item.type === 'application/pdf') {
				const url = URL.createObjectURL(item);
				window.open(url, '_blank');
			} else {
				const reader = new FileReader();
				reader.onload = (e: any) => {
					this.setState({ visible_file: true, file: e.target.result });
				};
				reader.readAsDataURL(item);
			}
		}
		else {
			if (item?.value?.type == 'application/pdf') {
				const url = URL.createObjectURL(item?.value);
				window.open(url, '_blank');
			}
			else {
				this.setState({
					visible_file: true,
					file: item?.dataURL
				});
			}
		}
	}

	render() {
		const { step, accessories, visible, visible_file, models, types } = this.state;
		const form: any = this.state.form;
		const is_edit = this.props.match.params?.id != null;
		const tires_type: any = this.state.accessory_types.find((i: any) => i.id == Constants.ACCESSORY_TYPES.TIRES_CONDITION);

		return (
			<div id="create-vehicle">
				<Modal
					className="modal-finish-vehicle"
					visible={visible}
					title=""
					withoutClose
					onClose={() => this.onClose()}
				>
					<div id="modal-finish-vehicle">
						<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
							<img src={Logo} className="logo" />
							<img src={FinishRegisterVehicleImage} />
						</div>
						<h3>Registro en proceso</h3>
						<p>Recuerda que para culminar el proceso de <strong>Registro del Vehículo</strong>, debes haber rellenado todos los campos obligatorios.</p>
						<button className="btn" onClick={() => {
							this.onClose();
						}}>
							Finalizar
						</button>
					</div>
				</Modal>

				<Viewer
					visible={visible_file}
					onClose={() => this.setState({ visible_file: false })}
					images={[{ src: this.state.file }]}
				/>

				<div className="container-buttons row">
					<div className="col-md">
						<button className={`item-button ${step == STEPS.FILES ? 'active' : ''}`} disabled={!is_edit} onClick={() => {
							this.setState({
								step: STEPS.FILES
							});
						}}>
							Certificado
						</button>
					</div>
					<div className="col-md">
						<button className={`item-button ${step == STEPS.MODEL ? 'active' : ''}`} disabled={!is_edit} onClick={() => {
							this.setState({
								step: STEPS.MODEL
							});
						}}>
							Modelo/Tipo
						</button>
					</div>
					<div className="col-md">
						<button className={`item-button ${step == STEPS.DETAILS ? 'active' : ''}`} disabled={!is_edit} onClick={() => {
							this.setState({
								step: STEPS.DETAILS
							});
						}}>
							Detalles
						</button>
					</div>
					<div className="col-md">
						<button className={`item-button ${step == STEPS.ACCESSORIES ? 'active' : ''}`} disabled={!is_edit} onClick={() => {
							this.setState({
								step: STEPS.ACCESSORIES
							});
						}}>
							Accesorios
						</button>
					</div>
					<div className="col-md">
						<button className={`item-button ${step == STEPS.PHOTOS ? 'active' : ''}`} disabled={!is_edit} onClick={() => {
							this.setState({
								step: STEPS.PHOTOS
							});
						}}>
							Fotos
						</button>
					</div>
					<div className="col-md">
						<button className={`item-button ${step == STEPS.DOCUMENTS ? 'active' : ''}`} disabled={!is_edit} onClick={() => {
							this.setState({
								step: STEPS.DOCUMENTS
							});
						}}>
							Documentos
						</button>
					</div>
					{
						!is_edit && (
							<div className="col-md">
								<div className={`item-button ${step == STEPS.APPOINTMENT ? 'active' : ''}`}>
									Cita
								</div>
							</div>
						)
					}
				</div>

				<div className="container-white">
					{
						step > STEPS.FILES && form.brand && (
							<div className="container-data">
								<img src={TruckIcon} />
								<p>{form.brand} / {form.color} / {form.year} / {form.model} / {form.category}</p>
							</div>
						)
					}

					{
						step == STEPS.FILES && (
							<div className="files-container">
								<DocUpload
									label="Certificado de Registro de Vehículo"
									isRequired
									showImage
									file={form.vehicle_card?.file ? form.vehicle_card?.file : form.vehicle_card?.defaultValue ? (Constants.BASE_STORAGE + form?.vehicle_card?.defaultValue?.file) : ''}
									message1={this.props.match.params?.id ? 'Cambiar archivo (Opcional)' : `Arrastre y suelte su imagen aquí`}
									message2={'Este documento debe ser legible. Formatos aceptados: JPG, PNG o PDF. No debe superar los 10mb'}
									onUpload={f => this.onChangeFile(f, 'vehicle_card')}
								/>
								{
									this.props.match.params?.id && (
										<button className="btn btn-icon btn-icon-init" onClick={() => {
											// @ts-ignore
											this.viewFile(form.vehicle_card?.file ? form.vehicle_card?.file : form.vehicle_card?.defaultValue ? (Constants.BASE_STORAGE + form?.vehicle_card?.defaultValue?.file) : '');
										}}>
											Ver
											<img src={EyeIcon} />
										</button>
									)
								}

								<div className="check-option">
									<div className="title-check">
										<p className="bold">¿El título esta a tu nombre?</p>
										<div className="check-container">
											<div>
												<div className={`check ${form.title_owner ? 'active' : ''}`} onClick={() => {
													this.setState({
														form: {
															...this.state.form,
															title_owner: !this.state.form.title_owner
														}
													});
												}}>
													<div className="check-circle"></div>
												</div>
											</div>
											<p className="label-status">{form.title_owner ? 'Si' : 'No'}</p>
										</div>
									</div>
									<p>Si el título no esta a tu nombre, por favor carga el título de compraventa</p>
								</div>

								{
									!this.state.form.title_owner && (
										<>
											<DocUpload
												label="Título de Compra Venta"
												isRequired
												showImage
												file={form.purchase_title?.file ? form.purchase_title?.file : form.purchase_title?.defaultValue ? (Constants.BASE_STORAGE + form?.purchase_title?.defaultValue?.file) : ''}
												message1={this.props.match.params?.id ? 'Cambiar archivo (Opcional)' : `Arrastre y suelte su archivo aquí`}
												message2="* Debe adjuntar este archivo en formato JPG, PNG o PDF. Peso máximo de este archivo (3mb)"
												onUpload={f => this.onChangeFile(f, 'purchase_title')}
											/>
											{
												this.props.match.params?.id && (
													<button className="btn btn-icon btn-icon-init" onClick={() => {
														// @ts-ignore
														this.viewFile(form.purchase_title?.file ? form.purchase_title?.file : form.purchase_title?.defaultValue ? (Constants.BASE_STORAGE + form?.purchase_title?.defaultValue?.file) : '')
													}}>
														Ver
														<img src={EyeIcon} />
													</button>
												)
											}
										</>
									)
								}

								<DocUpload
									disabled={!form.vehicle_card?.file}
									label="Foto de Placa"
									isRequired
									showImage
									file={form.plate_file?.file ? form.plate_file?.file : form.plate_file?.defaultValue ? (Constants.BASE_STORAGE + form?.plate_file?.defaultValue?.file) : ''}
									message1={this.props.match.params?.id ? 'Cambiar archivo (Opcional)' : `Arrastre y suelte su imagen aquí`}
									onUpload={f => {
										if (!form.vehicle_card?.file) {
											Globals.showError("Para cargar la foto de la placa, por favor cargue primero el certificado de registro de vehículo");
											return;
										}
										this.onChangeFile(f, 'plate_file');
									}}
									message2="Para cargar la foto de la placa debe haber cargado el certificado de registro de vehículo. El número de placa debe coincidir en ambos documentos"
								/>
								{
									this.props.match.params?.id && (
										<button className="btn btn-icon btn-icon-init" onClick={() => {
											// @ts-ignore
											this.viewFile(form.plate_file?.file ? form.plate_file?.file : form.plate_file?.defaultValue ? (Constants.BASE_STORAGE + form?.plate_file?.defaultValue?.file) : '')
										}}>
											Ver
											<img src={EyeIcon} />
										</button>
									)
								}
							</div>
						)
					}
					{
						step == STEPS.MODEL && (
							<>
								<div className="row h-100">
									<div className="col-md-6 h-100">
										<div className="container-card h-100">
											<h5>Seleccionar Modelo</h5>
											<Input
												value={this.state.form.search_model}
												name="search_model"
												placeholder="Buscar"
												className="input-table"
												notMui
												searchRight
												onChange={(e: any) => {
													this.change(e);
												}} />
											<div style={{ overflowY: 'auto', height: '500px' }}>
												{
													models.filter((item: any) => item.name.toLowerCase().includes(this.state.form.search_model.toLowerCase())).map((item: any) => {
														return (
															<div className={`item-vehicle ${this.state.form.vehicle_model_id == item?.id ? 'active' : ''}`} onClick={() => {
																this.setState({
																	form: {
																		...this.state.form,
																		vehicle_model_id: item?.id,
																		vehicle_model_type_id: ''
																	}
																});
															}}>
																<div>
																	<p className="name">{item?.name}</p>
																	<img src={item?.image} />
																</div>
																<div style={{ borderLeft: `1px solid ${Colors.gray}` }}>
																	<p><strong>Capacidad:</strong> {item?.capacity}</p>
																	<p><strong>M³:</strong> {item?.volume}</p>
																	<p><strong>Cant. de Paletas:</strong> {item?.quantity}</p>
																</div>
																{
																	this.state.form.vehicle_model_id == item?.id && (
																		<div className="active-container">
																			<img src={CheckTruckIcon} />
																		</div>
																	)
																}
															</div>
														)
													})
												}
											</div>
										</div>
									</div>
									<div className="col-md-6 h-100">
										<div className="container-card h-100">
											<h5>Seleccionar Tipo</h5>
											<Input
												value={this.state.form.search_type}
												name="search_type"
												placeholder="Buscar"
												className="input-table"
												notMui
												searchRight
												onChange={(e: any) => {
													this.change(e);
												}} />
											<div style={{ overflowY: 'auto', height: '500px' }}>
												{
													!this.state.form.vehicle_model_id ? (
														<p className="no-model">Seleccione un modelo</p>
													) : (
														<>
															{
																types?.filter((item: any) => item.name.toLowerCase().includes(this.state.form.search_type.toLowerCase())).filter((i: any) => i.model == this.state.form.vehicle_model_id).map((item: any) => {
																	return (
																		<div className={`item-vehicle ${this.state.form.vehicle_model_type_id == item?.id ? 'active' : ''}`} onClick={() => {
																			this.setState({
																				form: {
																					...this.state.form,
																					vehicle_model_type_id: item?.id
																				}
																			});
																		}}>
																			<div>
																				<p className="name">{item?.name}</p>
																				<img src={item?.image} />
																			</div>
																			<div style={{ borderLeft: `1px solid ${Colors.gray}` }}>
																				<p><strong>Largo:</strong> {item?.length}</p>
																				<p><strong>Ancho:</strong> {item?.width}</p>
																				<p><strong>Alto:</strong> {item?.height}</p>
																			</div>
																			{
																				this.state.form.vehicle_model_type_id == item?.id && (
																					<div className="active-container">
																						<img src={CheckTruckIcon} />
																					</div>
																				)
																			}
																		</div>
																	)
																})
															}
														</>
													)
												}
											</div>
										</div>
									</div>
								</div>
							</>
						)
					}
					{
						step == STEPS.DETAILS && (
							<div className="container-details">
								<Input
									type="decimal-negative"
									label="Ingresa a que temperatura mínima trabaja tu cava (°C)"
									value={this.state.form.temperature}
									name="temperature"
									placeholder="eje: -10 / 10"
									notMui
									onChange={(e: any) => {
										this.change(e);
									}} />
								<p className="note">Para temperaturas bajo cero usar signo negativo</p>

								<h5>Dimensiones de cava o furgón</h5>

								<div className="row">
									<div className="col-md-4">
										<Input
											type="decimal"
											label="Largo de la cava (metros)"
											value={this.state.form.length}
											name="length"
											notMui
											onChange={(e: any) => {
												this.change(e);
											}} />
									</div>
									<div className="col-md-4">
										<Input
											type="decimal"
											label="Ancho de la cava (metros)"
											value={this.state.form.width}
											name="width"
											notMui
											onChange={(e: any) => {
												this.change(e);
											}} />
									</div>
									<div className="col-md-4">
										<Input
											type="decimal"
											label="Alto de la cava (metros)"
											value={this.state.form.height}
											name="height"
											notMui
											onChange={(e: any) => {
												this.change(e);
											}} />
									</div>
								</div>

								<h5>Dimensiones de plataforma</h5>

								<div className="row">
									<div className="col-md-6">
										<Input
											type="decimal"
											label="Largo de la plataforma (metros)"
											value={this.state.form.platform_length}
											name="platform_length"
											notMui
											onChange={(e: any) => {
												this.change(e);
											}} />
									</div>
									<div className="col-md-6">
										<Input
											type="decimal"
											label="Ancho de la plataforma (metros)"
											value={this.state.form.platform_width}
											name="platform_width"
											notMui
											onChange={(e: any) => {
												this.change(e);
											}} />
									</div>
								</div>
							</div>
						)
					}
					{
						step == STEPS.ACCESSORIES && tires_type && (
							<>
								<div className="check-icon-container">
									<div className="icon-container">
										<img src={Constants.BASE_PUBLIC + 'img/' + tires_type?.icon} />
									</div>
									<p className="accessories-title">{tires_type?.name}</p>
								</div>
								<div className="radios">
									<div className="container-radio" onClick={() => {
										this.setState({
											form: {
												...this.state.form,
												tires: '20'
											}
										});
									}}>
										<div>
											<div className={`radio ${this.state.form.tires == '20' ? 'active' : ''}`}></div>
										</div>
										<p>20%</p>
									</div>
									<div className="container-radio" onClick={() => {
										this.setState({
											form: {
												...this.state.form,
												tires: '50'
											}
										});
									}}>
										<div>
											<div className={`radio ${this.state.form.tires == '50' ? 'active' : ''}`}></div>
										</div>
										<p>50%</p>
									</div>
									<div className="container-radio" onClick={() => {
										this.setState({
											form: {
												...this.state.form,
												tires: '80'
											}
										});
									}}>
										<div>
											<div className={`radio ${this.state.form.tires == '80' ? 'active' : ''}`}></div>
										</div>
										<p>80% o más</p>
									</div>
								</div>
								<hr />
								{
									this.state.accessory_types.map((type: any) => {
										if (accessories.filter((i: any) => i.type.toString() == type.id).length > 0) {
											return (
												<>
													<div className="row">
														<div className="col-12">
															<div className="check-icon-container">
																<div className="icon-container">
																	<img src={Constants.BASE_PUBLIC + 'img/' + type?.icon} />
																</div>
																<p className="accessories-title">{type.name}</p>
															</div>
														</div>
														{
															accessories?.map((item: any, index: number) => {
																if (item.type.toString() == type?.id) {
																	return (
																		<div className="col-md-3">
																			<div className="check-border">
																				<p className="check-title">{item.name}</p>
																				<div className="check-container">
																					<div className={`check ${item.value ? 'active' : ''}`} onClick={() => {
																						let accessories: any = [...this.state.accessories];
																						accessories[index].value = !accessories[index].value;
																						this.setState({
																							accessories
																						});
																					}}>
																						<div className="check-circle"></div>
																					</div>
																					<p className="label-status">{item.value ? 'Si' : 'No'}</p>
																				</div>
																			</div>
																		</div>
																	)
																}
															})
														}
													</div>
													<hr />
												</>
											)
										}
									})
								}
							</>
						)
					}
					{
						step == STEPS.DOCUMENTS && (
							<>
								<div className="row">
									{
										files?.map((item: any) => {
											// @ts-ignore
											const _file = this.state.form[item?.field]?.defaultValue?.file;

											return (
												<div className="col-md-6">
													<div className="document-item">
														<div className="row">
															<div className="col-md-8">
																<div className="container-title-document">
																	<p>{item?.name}</p>
																	<img className={(form[item.field]?.file || _file) ? 'active' : ''} src={(form[item.field]?.file || _file) ? CheckCircleIcon : CancelCircleIcon} />
																</div>
															</div>
															<div className="col-md-4">
																{
																	item.field != 'vehicle_card' && (
																		<PaperClip
																			className="btn-icon"
																			successText="Adjuntar"
																			pendingText="Adjuntar"
																			value={''}
																			formats={['png', 'jpg', 'pdf', 'jpeg']}
																			onChange={(e: any) => {
																				this.setState({
																					form: {
																						...this.state.form,
																						[item.field]: {
																							...form[item.field],
																							file: e.target
																						}
																					}
																				}, () => {
																					this.changeDocument(item);
																				});
																			}}>
																			Adjuntar
																			<img src={UploadIcon} />
																		</PaperClip>
																	)
																}
																<button className="btn btn-icon" disabled={!form[item.field]?.file && !_file} onClick={() => {
																	// @ts-ignore
																	this.viewFile(form[item.field]?.file || (Constants.BASE_STORAGE + _file))
																}}>
																	Ver
																	<img src={EyeIcon} />
																</button>
															</div>
														</div>

														{
															item.field != 'vehicle_card' && (
																<DatePicker
																	label="F. Expiración"
																	placeholder="--_--"
																	minDate={moment().add(1, 'day').toDate()}
																	disabled={true}
																	onChange={(expiration_date: string) => {
																		// this.setState({
																		//   form: {
																		//   	...this.state.form,
																		//   	[item.field]: {
																		//   		...form[item.field],
																		//   		expiration_date
																		//   	}
																		//   }
																		// });
																	}}
																	value={form[item.field]?.expiration_date}
																/>
															)
														}
													</div>
												</div>
											)
										})
									}
								</div>
							</>
						)
					}
					{
						step == STEPS.PHOTOS && (
							<>
								<div className="row row-photos">
									{
										photos?.map((item: any) => {
											// @ts-ignore
											const _file = this.state.form[item?.field]?.defaultValue?.file;

											return (
												<div className="col-md-6">
													<PaperClip
														onChange={(e: any) => {
															this.setState({
																form: {
																	...this.state.form,
																	[item.field]: {
																		...form[item.field],
																		file: e.target
																	}
																}
															});
														}}>
														<img src={form[item.field]?.file?.dataURL || (_file ? (Constants.BASE_STORAGE + _file) : item?.icon)} className={form[item.field]?.file || _file ? 'active' : ''} />
														<div className="container-camera">
															<img src={CameraIcon} className="camera" />
														</div>
													</PaperClip>

													<p className="photo-title">{item?.name}</p>
												</div>
											)
										})
									}
								</div>
							</>
						)
					}
					{
						step == STEPS.APPOINTMENT && (
							<>
								<div className="appointment">
									<h3>Cita de Certificación de Vehículo</h3>
									<p>Debemos verificar el vehículo de forma presencial, para ello contamos con más de 13 puntos de certificación a nivel nacional. Selecciona el punto más cercano a tu localidad, para agendar una cita</p>
									<Select
										name="agency_id"
										placeholder="Agencia"
										onChange={(e: any) => this.change(e)}
										value={form.agency_id}
										options={this.state.agencies?.map((item: any) => {
											return {
												value: item.id,
												label: item?.name
											}
										})}
									/>
								</div>
							</>
						)
					}
				</div>

				<div className="text-right" style={{ maxWidth: '1100px', margin: '0 auto' }}>
					<div className="row row-buttons">
						<div className="col-md-6">
							<button className="btn" onClick={() => this.back()}>
								Atras
							</button>
						</div>
						<div className="col-md-6">
							<button className="btn btn-blue" onClick={() => {
								this.next();
							}}>
								Continuar
							</button>
						</div>
					</div>
				</div>
			</div>
		)
	}
}

export default connect((state: RootState) => {
	return {
		user: state.user
	}
})(Create);